import type {
  AttentionItem,
  CaseSummary,
  CurrentUser,
  DashboardSummary,
  MyTasks,
  QuoteSummary,
} from '@/types/domain'

/* Fixtures for working on the UI with no server at all.
 *
 * Figures are taken verbatim from the UX mockups so a rendered screen can be
 * diffed against `ASA Gateway UI mockups/Dashboard.dc.html` directly. These are
 * NOT seed data and must never become database rows — the real Master File
 * import is a separate, validated path (AD-16).
 *
 * The API is up and serves this same dataset (backend/internal/showcase), so
 * the real client path is the default and mocks are opt-in: set
 * `VITE_USE_MOCKS=true` for offline UI work. The two datasets are kept in step
 * by hand until each showcase route is replaced by its real module, at which
 * point the corresponding fixture here loses its reason to exist. */

export const USE_MOCKS = import.meta.env.VITE_USE_MOCKS === 'true'

export const MOCK_SESSION: CurrentUser = {
  id: 'u-1',
  name: 'Arslan Kerimov',
  email: 'arslan@asagateway.com',
  role: 'super-admin',
  grants: { manageClients: true, manageParticipants: true, manageTransport: true },
  // The mock founder holds the superuser flag, so `can()` short-circuits and
  // the enumerated list stays empty — exactly as the server serves it. A mock
  // that listed every capability would pass checks the real payload passes for
  // a different reason, and would drift the moment a capability was added.
  capabilities: [],
  isSuperuser: true,
  language: 'en',
  // Unflagged, or every fixture screen would sit behind /change-password.
  mustChangePassword: false,
  seesAgentIdentity: true,
}

export const MOCK_DASHBOARD: DashboardSummary = {
  counters: {
    activeCases: 41,
    shipmentsThisMonth: 17,
    shipmentsDelta: 4,
    /* Real as of Story 4.6, so the fixture carries a figure rather than leaving
       the tile to render nothing offline. A LOSS, deliberately: the mock's job
       is to exercise the shapes a live screen has to survive, and a negative
       forecast is the one that drives the FR-52 minus row below — a fixture
       that only ever shows profit is a fixture nobody notices the sign code
       under. */
    forecastProfit: { amount: '-4820.00', currency: 'USD' },
    /* Real as of Story B7: billed this month, net of VAT, at booked USD. A
       figure with cents, so the offline tile exercises the grouping and the
       two decimals a live month produces. */
    turnover: { amount: '1334.56', currency: 'USD' },
  },

  /* Only the kinds the server computes: minus and stuck. The approval,
     overdue and quote-followup rows this fixture used to carry were the
     mockup's, and they needed a forecast profit, a rate approval and an
     invoice — none of which existed then (Stories 4.6, 4.7, 5.4). A fixture
     richer than the server is how the mock stops being a preview of the
     product and starts being a different product.

     The `payment-confirmation` row (QW-10) is gone with the server's (S-2b.2):
     the queue is the `payments-to-confirm` section of `MOCK_MY_TASKS` now, in
     the strip above this dashboard. */
  attention: [
    {
      id: 'stuck-ab-27',
      kind: 'stuck',
      recordKind: 'case',
      recordNumber: 'AB-27',
      title: '4',
      detail: 'Nurana Trading',
      href: '/cases/ab-27',
    },
    {
      id: 'stuck-mg-19',
      kind: 'stuck',
      recordKind: 'case',
      recordNumber: 'MG-19',
      title: '6',
      detail: 'Beg Logistics',
      href: '/cases/mg-19',
    },
  ],

  stuckDays: 3,

  /* A fixed instant, not new Date(). A fixture that stamps itself "now" would
     make the staleness indicator untestable — it could never be seen to go
     stale, which is the one thing it exists to show. */
  readAt: '2026-08-05T08:42:00Z',

  /* EMPTY, in step with readmodel.go's pendingFigures, which has named nothing
     since Story B7 took `turnover` off it (2026-10-09). Keeping this in step is
     what makes offline UI work represent the real screen. The entries that used
     to be here — forecastProfit, toCollect, toPay — were declared pending
     against stories that had already shipped, so this fixture went on rendering
     tiles as unavailable after the server started computing them. That is the
     failure this comment exists to prevent: a stale entry here is invisible,
     because a tile that says "not yet" looks deliberate. */
  pending: [],
}

export const MOCK_CASES: CaseSummary[] = [
  {
    id: 'ab-27',
    number: 'AB-27',
    clientName: 'Nurana Trading',
    clientId: 'cl-nurana',
    status: 'stuck',
    origin: 'Guangzhou',
    destination: 'Ashgabat',
    mode: 'rail',
    transportModeId: 'tr1',
    entityId: 'ie1',
    etd: '2026-07-12',
    eta: '2026-08-04',
    rate: { amount: '34500.00', currency: 'USD' },
    profit: { amount: '4820.00', currency: 'USD' },
    profitBasis: 'forecast',
    daysStalled: 4,
    ownerName: 'Merjen Atayeva',
    clientContactName: 'Maksat Hojayev',
    clientContactEmail: 'maksat@nurana.tm',
  },
  {
    id: 'mg-19',
    number: 'MG-19',
    clientName: 'Beg Logistics',
    clientId: 'cl-beg',
    status: 'in-progress',
    origin: 'Ningbo',
    destination: 'Türkmenbaşy',
    mode: 'sea',
    etd: '2026-07-18',
    eta: '2026-08-12',
    rate: { amount: '28900.00', currency: 'USD' },
    profit: { amount: '3110.00', currency: 'USD' },
    profitBasis: 'forecast',
    ownerName: 'Sun Di',
    clientContactName: 'Begench Amanov',
    clientContactEmail: 'beg@beglogistics.tm',
  },
  {
    id: 'ab-11',
    number: 'AB-11',
    clientName: 'Nurana Trading',
    clientId: 'cl-nurana',
    status: 'in-progress',
    origin: 'Yiwu',
    destination: 'Mary',
    mode: 'road',
    etd: '2026-07-02',
    eta: '2026-07-29',
    rate: { amount: '16400.00', currency: 'USD' },
    profit: { amount: '-820.00', currency: 'USD' },
    profitBasis: 'forecast',
    ownerName: 'Merjen Atayeva',
    clientContactName: 'Maksat Hojayev',
    clientContactEmail: 'maksat@nurana.tm',
  },
  {
    id: 'ab-24',
    number: 'AB-24',
    clientName: 'Altyn Asyr Import',
    clientId: 'cl-altyn',
    status: 'delivered',
    origin: 'Shanghai',
    destination: 'Ashgabat',
    mode: 'multimodal',
    etd: '2026-06-20',
    eta: '2026-07-21',
    rate: { amount: '51200.00', currency: 'USD' },
    profit: { amount: '7640.00', currency: 'USD' },
    profitBasis: 'forecast',
    ownerName: 'Aygul Nazarova',
    clientContactName: 'Gurban Saparov',
    clientContactEmail: 'gurban@altynasyr.tm',
  },
  {
    id: 'mg-17',
    number: 'MG-17',
    clientName: 'Caspian Steel',
    clientId: 'cl-caspian',
    status: 'financially-closed',
    origin: 'Qingdao',
    destination: 'Türkmenabat',
    mode: 'rail',
    etd: '2026-05-28',
    eta: '2026-06-24',
    rate: { amount: '42800.00', currency: 'USD' },
    /* Financially closed, so the row's figure is its CASH (S-6) — and a
       different number from what its forecast was, so the offline board shows
       the closed lane is not printing a forecast under the word «Прибыль». */
    profit: { amount: '6040.00', currency: 'USD' },
    profitBasis: 'final',
    ownerName: 'Sun Di',
  },
]

export const MOCK_QUOTES: QuoteSummary[] = [
  {
    id: 'mg-q-14',
    clientId: 'cl-beg',
    pricing: { state: 'none' },
    number: 'MG-Q-14',
    clientName: 'Beg Logistics',
    // The header's one-line description, which the list column and the board
    // card both read. Absent on AB-Q-32 below, so the empty cell is exercised.
    cargoDescription: 'Ceramic tiles and grout, palletised',
    status: 'sent',
    origin: 'Yiwu',
    destination: 'Ashgabat',
    mode: 'rail',
    sell: { amount: '31200.00', currency: 'USD' },
    margin: { amount: '4180.00', currency: 'USD' },
    marginPercent: '13.4',
    validUntil: '2026-07-31',
    // Awaiting the client, so an offer has issued and the card cites it.
    revisionLabel: 'A',
    ownerName: 'Sun Di',
    // Complete, so the card flags only the client's silence.
    cargo: { positions: 2, packages: 2, packageType: "40'HC", blocked: 0 },
  },
  {
    id: 'ab-q-32',
    clientId: 'cl-altyn',
    // Waiting on Pricing (S-2a), so the board's draft lane shows the chip.
    pricing: {
      state: 'requested',
      assigneeId: 'u-4',
      assigneeName: 'Sun Di',
      requestedAt: '2026-10-09T08:30:00Z',
      requestedByName: 'Merjen Atayeva',
    },
    number: 'AB-Q-32',
    clientName: 'Altyn Asyr Import',
    status: 'draft',
    origin: 'Ningbo',
    destination: 'Mary',
    mode: 'sea',
    sell: { amount: '22600.00', currency: 'USD' },
    margin: { amount: '2940.00', currency: 'USD' },
    marginPercent: '13.0',
    ownerName: 'Merjen Atayeva',
    // One position short of sendable — the amber flag the board is for. A
    // picker code (F3), so the card words it; "40'HC" above is typed text.
    cargo: { positions: 1, packages: 4, packageType: 'pallet', blocked: 1 },
  },
  {
    id: 'ab-q-31',
    clientId: 'cl-caspian',
    pricing: { state: 'none' },
    number: 'AB-Q-31',
    clientName: 'Caspian Steel',
    cargoDescription: 'Hot-rolled steel coil, mixed gauges',
    status: 'converted',
    origin: 'Guangzhou',
    destination: 'Türkmenbaşy',
    mode: 'multimodal',
    sell: { amount: '47500.00', currency: 'USD' },
    margin: { amount: '6820.00', currency: 'USD' },
    marginPercent: '14.4',
    validUntil: '2026-08-06',
    // Accepted after one round of bargaining: the client holds Rev B.
    revisionLabel: 'B',
    ownerName: 'Merjen Atayeva',
    // Mixed packing: the card counts positions rather than naming one type.
    cargo: { positions: 3, packages: 9, blocked: 0 },
  },
]

export const MOCK_ATTENTION: AttentionItem[] = MOCK_DASHBOARD.attention

/* «Мои задачи» as the mock founder would be served them (S-2b.1, S-2b.2). The
   superuser holds every gate, so all nine sections are present — two of them
   empty, which is how a held section with nothing waiting arrives. The records
   are the quote and case fixtures above, by id, so a row opens a page that
   exists on mocks. The `expiring-soon` row (FR-16) carries `validUntil` and no
   `since`, as the wire does; its day is set against this `readAt`, not
   MG-Q-14's own July validity, so the row reads as a warning should.

   The two finance sections agree with the finance fixtures, because three
   surfaces disagreeing offline would hide the same disagreement online:
   `rate-approvals` is `MOCK_APPROVALS` (ap2 then ap1 — oldest request first,
   as the composer sorts), with no reason and no client, as the item carries
   neither; `payments-to-confirm` counts the one pending row in
   `MOCK_PAYMENTS` (pm3), which the sidebar badge and the Payments tab's chip
   count through the same `status` filter. */
export const MOCK_MY_TASKS: MyTasks = {
  readAt: '2026-10-11T08:00:00Z',
  sections: [
    {
      key: 'pricing-requested',
      count: 1,
      items: [
        {
          kind: 'quote',
          id: 'ab-q-32',
          number: 'AB-Q-32',
          clientName: 'Altyn Asyr Import',
          since: '2026-10-09T08:30:00Z',
        },
      ],
    },
    { key: 'pricing-ready', count: 0, items: [] },
    {
      key: 'awaiting-client',
      count: 1,
      items: [
        {
          kind: 'quote',
          id: 'ab-q-31',
          number: 'AB-Q-31',
          clientName: 'Caspian Steel',
          since: '2026-10-02T11:15:00Z',
        },
      ],
    },
    {
      key: 'expiring-soon',
      count: 1,
      items: [
        {
          kind: 'quote',
          id: 'mg-q-14',
          number: 'MG-Q-14',
          clientName: 'Beg Logistics',
          validUntil: '2026-10-14',
        },
      ],
    },
    { key: 'case-no-legs', count: 0, items: [] },
    {
      key: 'case-no-vehicle',
      count: 1,
      items: [
        {
          kind: 'case',
          id: 'ab-27',
          number: 'AB-27',
          clientName: 'Nurana Trading',
          legId: 'leg-ab-27-1',
          since: '2026-09-30T07:00:00Z',
        },
      ],
    },
    {
      key: 'case-no-actuals',
      count: 1,
      items: [
        {
          kind: 'case',
          id: 'mg-19',
          number: 'MG-19',
          clientName: 'Beg Logistics',
          since: '2026-09-21T10:00:00Z',
        },
      ],
    },
    {
      key: 'rate-approvals',
      count: 2,
      items: [
        {
          kind: 'approval',
          id: 'ap2',
          number: 'AB-11',
          since: '2026-07-29T08:15:00Z',
          requesterName: 'Merjen Atayeva',
          amount: { amount: '-600.00', currency: 'USD' },
        },
        {
          kind: 'approval',
          id: 'ap1',
          number: 'MG-19',
          since: '2026-07-30T14:20:00Z',
          requesterName: 'Sun Di',
          amount: { amount: '1400.00', currency: 'USD' },
        },
      ],
    },
    { key: 'payments-to-confirm', count: 1, items: [] },
  ],
}

/* MOCK_ACTIVITY is gone with the activity feed itself: no table records who did
   what, so the entries could only be invented (Epic 8). */
